/**
 * PostImportLearningDialog
 * Presented after successful import to save learning for future imports
 * Options: Save Now, Save Later, Decline
 */

import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Brain,
  Save,
  Clock,
  XCircle,
  CheckCircle2,
  TrendingUp,
  Sparkles,
  FileText,
  Info,
} from "lucide-react";
import { trpc } from "@/lib/trpc";

interface FormatLearning {
  documentType: string;
  delimiter: string;
  encoding?: string;
  headerRow?: number;
  dateFormat?: string;
  amountFormat?: string;
  columnMappings?: Record<string, any>;
}

interface ImportResults {
  transactionsImported: number;
  transactionsSkipped: number;
  manualCorrectionsNeeded?: number;
}

interface PostImportLearningDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: number;
  sessionId: number;
  formatLearning: FormatLearning;
  togglePreferences?: Record<string, boolean>;
  importResults: ImportResults;
  isFirstImport?: boolean;
  onComplete?: (decision: "saved" | "deferred" | "declined") => void;
}

export default function PostImportLearningDialog({
  open,
  onOpenChange,
  companyId,
  sessionId,
  formatLearning,
  togglePreferences,
  importResults,
  isFirstImport = false,
  onComplete,
}: PostImportLearningDialogProps) {
  const [userNotes, setUserNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const saveLearning = trpc.enhancedImport.saveImportLearning.useMutation();

  const handleDecision = async (
    decision: "saved" | "deferred" | "declined"
  ) => {
    setSaving(true);
    try {
      await saveLearning.mutateAsync({
        companyId,
        sessionId,
        decision,
        formatLearning: decision === "saved" ? formatLearning : undefined,
        togglePreferences: decision === "saved" ? togglePreferences : undefined,
        importResults,
        userNotes: userNotes.trim() || undefined,
      });

      onComplete?.(decision);
      onOpenChange(false);
    } catch (error) {
      console.error("[PostImportLearningDialog] Save failed:", error);
    } finally {
      setSaving(false);
    }
  };

  const successRate = Math.round(
    (importResults.transactionsImported /
      (importResults.transactionsImported +
        importResults.transactionsSkipped)) *
      100
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-primary" />
            Save Import Learning
          </DialogTitle>
          <DialogDescription>
            {isFirstImport
              ? "This is the first time importing this format. Save the settings for faster future imports."
              : "Would you like to save any improvements from this import?"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Import Summary */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-success" />
                Import Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <div className="text-2xl font-bold text-success">
                    {importResults.transactionsImported}
                  </div>
                  <div className="text-xs text-muted-foreground">Imported</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-muted-foreground">
                    {importResults.transactionsSkipped}
                  </div>
                  <div className="text-xs text-muted-foreground">Skipped</div>
                </div>
                <div>
                  <div className="text-2xl font-bold">{successRate}%</div>
                  <div className="text-xs text-muted-foreground">
                    Success Rate
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* What Will Be Saved */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <FileText className="h-4 w-4" />
                What Will Be Saved
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-2">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs">
                  Format
                </Badge>
                <span>{formatLearning.documentType}</span>
              </div>
              {formatLearning.delimiter && (
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">
                    Delimiter
                  </Badge>
                  <span className="font-mono">
                    {formatLearning.delimiter === ";"
                      ? "Semicolon (;)"
                      : formatLearning.delimiter === ","
                        ? "Comma (,)"
                        : formatLearning.delimiter}
                  </span>
                </div>
              )}
              {formatLearning.dateFormat && (
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">
                    Date Format
                  </Badge>
                  <span className="font-mono">{formatLearning.dateFormat}</span>
                </div>
              )}
              {togglePreferences &&
                Object.keys(togglePreferences).length > 0 && (
                  <div className="flex items-start gap-2">
                    <Badge variant="outline" className="text-xs mt-0.5">
                      Tuning
                    </Badge>
                    <span>
                      {
                        Object.keys(togglePreferences).filter(
                          k => togglePreferences[k]
                        ).length
                      }{" "}
                      enabled rules
                    </span>
                  </div>
                )}
            </CardContent>
          </Card>

          {/* Optional Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes" className="text-sm flex items-center gap-2">
              <Info className="h-4 w-4" />
              Notes (optional)
            </Label>
            <Textarea
              id="notes"
              placeholder="Add any notes about this import (e.g., 'December statement with holiday transactions')"
              value={userNotes}
              onChange={e => setUserNotes(e.target.value)}
              rows={2}
              className="text-sm"
            />
          </div>

          {/* Benefit Alert */}
          {isFirstImport && (
            <Alert>
              <Sparkles className="h-4 w-4" />
              <AlertTitle>First-time Format</AlertTitle>
              <AlertDescription>
                Saving this learning will make future imports of this format
                significantly faster. The system will automatically apply your
                settings and tuning preferences.
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={() => handleDecision("declined")}
            disabled={saving}
            className="flex-1"
          >
            <XCircle className="h-4 w-4 mr-2" />
            Don't Save
          </Button>
          <Button
            variant="outline"
            onClick={() => handleDecision("deferred")}
            disabled={saving}
            className="flex-1"
          >
            <Clock className="h-4 w-4 mr-2" />
            Save Later
          </Button>
          <Button
            onClick={() => handleDecision("saved")}
            disabled={saving}
            className="flex-1"
          >
            {saving ? (
              <>
                <TrendingUp className="h-4 w-4 mr-2 animate-pulse" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Learning
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
